import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { adminApiHeader, finalRouteApi } from '../../components/CommonApis/CommonApiUrl';
import { Link } from 'react-router-dom';
import AdminBackNavigation from '../../components/CommonComponents/AdminBackNavigation';
import SweetAlert from 'react-bootstrap-sweetalert';
import { toast } from 'react-toastify';

export default function FieldsList() {
  const [fields, setFields] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);

  const fetchFields = async () => {
    try {
      const res = await axios.get(`${finalRouteApi}/custom-fields`, { headers: adminApiHeader });
      setFields(res.data?.fields || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    }
  };

  useEffect(() => {
    let mounted = true;
    async function init() {
      try {
        // Use the new dashboard endpoint to detect feature status
        const feat = await axios.get(`${finalRouteApi}/custom-fields/dashboard`, { headers: adminApiHeader });
        if (!mounted) return;
        if (!feat.data?.enabled) { setEnabled(false); setLoading(false); return; }
        setEnabled(true);
        await fetchFields();
      } catch (e) {
        // fallback: if dashboard check failed, try old endpoint (backward compat)
        try {
          const feat = await axios.get(`${finalRouteApi}/admin/features/custom-fields`, { headers: adminApiHeader });
          if (!mounted) return;
          if (!feat.data?.enabled) { setEnabled(false); setLoading(false); return; }
          setEnabled(true);
          await fetchFields();
        } catch (err) {
          setError(err?.response?.data?.message || err.message);
        }
      } finally { setLoading(false); }
    }
    init();
    return () => { mounted = false; };
  }, []);

  const handleDelete = async (id) => {
    setError('');
    try {
      await axios.delete(`${finalRouteApi}/custom-fields/${id}`, { headers: adminApiHeader });
      setFields(prev => prev.filter(f => f.id !== id));
      toast.success('Custom field deleted');
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setConfirmingId(null);
    }
  };

  if (loading) return <div>Loading…</div>;
  if (error) return (
    <div className="alert alert-warning">
      <div className="mb-2"><strong>Unable to reach Custom Fields service.</strong></div>
      <div className="mb-2">{error}</div>
      <div>Please check API connectivity and authentication, then refresh this page.</div>
    </div>
  );
  if (!enabled) return <div>Custom Fields addon is disabled.</div>;

  return (
    <div>
      <AdminBackNavigation path="/" text="Back to Dashboard" />
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <h2>Custom Fields</h2>
        <div>
          <Link to="/custom-fields/create" className="btn btn-primary">Add Field</Link>
          <Link to="/custom-fields/assign" className="btn btn-secondary" style={{marginLeft:8}}>Assign Fields</Link>
        </div>
      </div>
      {error && <div style={{color:'red'}}>{error}</div>}
      <table className="table">
        <thead><tr><th>Name</th><th>Key</th><th>Type</th><th>Required</th><th>Premium</th><th>Active</th><th>Actions</th></tr></thead>
        <tbody>
          {fields.map(f => (
            <tr key={f.id}>
              <td>{f.name}</td>
              <td>{f.key}</td>
              <td>{f.type}</td>
              <td>{f.required ? 'Yes' : 'No'}</td>
              <td>{f.premium_only ? 'Yes' : 'No'}</td>
              <td>{f.active ? 'Yes' : 'No'}</td>
              <td>
                <div className="d-flex gap-2">
                  <Link to={`/custom-fields/edit/${f.id}`} className="btn btn-link p-0">Edit</Link>
                  <button className="btn btn-link text-danger p-0 ms-2" onClick={() => setConfirmingId(f.id)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Confirm Delete Modal */}
      <SweetAlert
        show={!!confirmingId}
        warning
        showCancel
        title="Delete custom field?"
        onConfirm={() => handleDelete(confirmingId)}
        onCancel={() => setConfirmingId(null)}
        confirmBtnBsStyle="danger"
        confirmBtnText="Yes, delete"
        cancelBtnText="Cancel"
      >
        This will remove the field definition and any associated assignments/values. This action cannot be undone.
      </SweetAlert>
    </div>
  );
}
